<template>
  <div>
    <InfoCard>
      En utleierprofil er leietakers motpart i leieforholdet; det være seg en privatperson, et ektepar eller et
      aksjeselskap. De som oppretter, og eier, utleierprofilen står fritt til å velge dens funksjon. Du kan samle så
      mange eller så få eiendommer og leieforhold som du selv ønsker på en utleierprofil. Se på det som en måte å styre
      din portefølje av leieforhold.
    </InfoCard>

    <BlockContainer v-if="getLandlordProfile?.type === 'private'" class="p-4 mt-4">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <h5>Verifisering av utleierprofil</h5>
        <Icon :name="iconName" :size="iconSize" :class="[landlordIdentity ? 'text-primary' : 'text-danger']" />
      </div>

      <p v-if="landlordIdentity">Din utleierprofil er verifisert med BankID.</p>
      <div v-else>
        <p>
          Ettersom du forvalter dine leieforhold gjennom oss, er det viktig at vi blir litt bedre kjent med deg. Når vi
          har en sikker identitet på både utleier og leietaker, kan vi skape tryggere leieforhold og tilby dere en bedre
          og sikrere tjeneste.
        </p>

        <p>Trykk på knappen under for å starte prosessen.</p>

        <p v-if="isUserSudo" class="text-danger font-weight-bold">Denne funksjonaliteten fungerer ikke via sudo</p>

        <hl-Button :href="verificationAppURL" rel="noopener" target="_blank" :disabled="isUserSudo">
          Verifiser
        </hl-Button>
      </div>
    </BlockContainer>

    <div class="grid mt-4">
      <BlockContainer class="p-4">
        <div class="d-flex align-items-center">
          <i class="h3 budicon-user-b" />
          <h5 class="ml-4">Navn & adresse</h5>
        </div>
        <p class="mt-3">
          I Navn & adresse kan du se og endre informasjon som utleierprofilens navn og faktura- og postadresse.
        </p>
        <div class="d-flex justify-content-center mt-5">
          <button
            class="btn btn-sm btn-outline-secondary d-flex align-items-center"
            @click="goToRoute('landlord_profile_name_address')"
          >
            Navn & adresse
            <i class="ml-2 budicon-arrow-right" />
          </button>
        </div>
      </BlockContainer>
      <BlockContainer class="p-4">
        <div class="d-flex align-items-center">
          <i class="h3 budicon-user-b" />
          <h5 class="ml-4">Preferanser</h5>
        </div>
        <p class="mt-3">
          I Preferanser kan du se og endre hvilke bankkontoer som benyttes for utbetaling av husleien, samt definere
          husordensregler og bestemmelser som skal brukes i dine husleiekontrakter som opprettes fremover. Du kan også
          definere en utleierprofil som standard, dvs at det er denne du ser først når du logger inn på Husleie.no.
        </p>
        <div class="d-flex justify-content-center mt-5">
          <button
            class="btn btn-sm btn-outline-secondary d-flex align-items-center"
            @click="goToRoute('landlord_profile_default_settings')"
          >
            Preferanser
            <i class="ml-2 budicon-arrow-right" />
          </button>
        </div>
      </BlockContainer>
    </div>
    <div class="row no-gutters">
      <BlockContainer class="col p-4 mt-4">
        <div class="d-flex align-items-center">
          <i class="h3 budicon-user-b" />
          <h5 class="ml-4">Faktura & Abonnement</h5>
        </div>
        <p class="mt-3">
          I Faktura & Abonnement finner du en oversikt over alle dine aktive forvaltningsabonnement, og disse kan du
          oppgradere eller nedgradere til andre abonnementsnivå. Du finner også kopi av fakturaene vi har sendt til deg
          for betaling av våre tjenester.
        </p>
        <div class="d-flex justify-content-center mt-5">
          <button
            class="btn btn-sm btn-outline-secondary d-flex align-items-center"
            @click="goToRoute('landlord_profile_invoicing')"
          >
            Faktura & Abo
            <i class="ml-2 budicon-arrow-right" />
          </button>
        </div>
      </BlockContainer>
      <BlockContainer v-if="isProfessionalUser" class="col p-4 mt-4 ml-4">
        <div class="d-flex align-items-center">
          <i class="h3 budicon-user-b" />
          <h5 class="ml-4">API-instillinger</h5>
        </div>
        <p class="mt-3">
          Utleierprofiler som har avtale om overføring av data til økonomisystemer kan i API-innstillinger konfigurere
          malen for KID-nummer du vil skal benyttes ved utbetaling av husleie til din(e) bankkonto.
        </p>
        <div class="d-flex justify-content-center mt-5">
          <button
            class="btn btn-sm btn-outline-secondary d-flex align-items-center"
            @click="goToRoute('landlord_profile_settings')"
          >
            API-instillinger
            <i class="ml-2 budicon-arrow-right" />
          </button>
        </div>
      </BlockContainer>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import BlockContainer from '@/components/BlockContainer.vue';
import InfoCard from '@/components/InfoCard.vue';
import { AppRedirect } from '@modules/husleie-framework/src/enums/AppRedirect.enum';
import RedirectOperations from '@modules/husleie-framework/src/static-classes/RedirectOperations.class';
import Icon, { IconName, IconSize } from '@modules/design-system/components/Icon';
import { LandlordApi } from '@modules/husleieapi2';

export default Vue.extend({
  name: 'LandlordProfileOverview',
  data() {
    return {
      landlordIdentity: undefined,
    };
  },
  props: {
    landlordId: {
      type: String,
      required: true,
    },
  },
  components: {
    BlockContainer,
    InfoCard,
    Icon,
  },
  watch: {
    getLandlordProfile: {
      handler(value) {
        if (!value) {
          return;
        }

        if (value.type === 'private') {
          this.fetchLandlordIdentity();
        }
      },
      immediate: true,
    },
  },
  computed: {
    ...mapGetters(['getLandlordProfileMeta', 'isProfessionalUser', 'isUserSudo']),

    getLandlordProfile() {
      return this.getLandlordProfileMeta(this.landlordId);
    },
    verificationAppURL() {
      return RedirectOperations.getURL(AppRedirect.BANKID_VERIFICATION);
    },
    iconName() {
      return IconName.InterfaceVerified;
    },
    iconSize() {
      return IconSize.Large;
    },
  },
  methods: {
    goToRoute(routeName) {
      this.$router.push({
        name: routeName,
        params: {
          landlordId: this.landlordId,
        },
      });
    },
    async fetchLandlordIdentity() {
      try {
        const api = new LandlordApi();
        this.landlordIdentity = await api.getLandlordIdentity(this.landlordId);
      } catch (error) {
        if (error.response.status !== 404) {
          throw new Error('Failed calling `getLandlordIdentity`');
        }
      }
    },
    goToVerificationApp() {
      RedirectOperations.goToURL(AppRedirect.BANKID_VERIFICATION, {}, true);
    },
  },
});
</script>

<style lang="scss" scoped>
.grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem 1rem;
}
</style>
